import { useState, Fragment, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createStore } from "../../api/mutations";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import { safeStorage } from "../../utils/storage";
import { currencyOptionsFor } from "../../constants/currencies";
import { centerCrop, makeAspectCrop } from "react-image-crop";
import transparentBackground from "../../assets/transparent.jpg";
import defaultLogo from "../../assets/logo-placeholder.png";
import StoreTimeZoneField from "../../components/StoreTimeZoneField";
import { getBrowserTimeZone } from "../../utils/timeZones";
import { isValidTimeZone } from "../../utils/zonedTime";

// The browser's zone, if it is one worth pre-filling: a zone this runtime's
// Intl resolves, and not "Etc/Unknown", which browsers report when they can't
// work out the zone and which the backend rejects. Otherwise null, so the
// field starts empty and the required check asks for a pick.
const prefillTimeZone = () => {
  const zone = getBrowserTimeZone();
  return zone && zone !== "Etc/Unknown" && isValidTimeZone(zone) ? zone : null;
};

// The backend answers an unknown zone with 422 and full messages such as
// "Time zone is not included in the list" (attribute names aren't translated
// there, so the English attribute is what to match).
const createErrorMessages = (error) => {
  if (error?.response?.status !== 422) return [];
  const messages = error.response.data?.errors;
  return Array.isArray(messages) ? messages.map(String) : [];
};
const isTimeZoneMessage = (message) => /time zone/i.test(message);

export default function CreateStore() {
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [crop, setCrop] = useState();
  const [currency, setCurrency] = useState("€");
  // A new store starts on the merchant's browser zone and cannot be created
  // without one (Serhat, 2026-10-21). Kept in the option list even if Intl's
  // list spells it differently.
  const [browserTimeZone] = useState(prefillTimeZone);
  const [timeZone, setTimeZone] = useState(() => browserTimeZone || "");
  const [primaryColor, setPrimaryColor] = useState("#3B82F6"); // Default indigo
  const [secondaryColor, setSecondaryColor] = useState("#1E40AF"); // Default darker indigo
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF"); // Default white

  const [errors, setErrors] = useState({
    name: "",
    logo: "",
    currency: "",
    timeZone: "",
  });

  const [touched, setTouched] = useState({
    name: false,
  });

  useEffect(() => {
    const fetchLogo = async () => {
      const response = await fetch(defaultLogo);
      const blob = await response.blob();
      setLogo(blob);
      // setPreviewUrl(URL.createObjectURL(blob));
    };
    fetchLogo();
  }, []);

  const navigate = useNavigate();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { updateSelectedStore } = useStore();

  const createStoreMutation = useMutation({
    mutationFn: createStore,
    onSuccess: async (data) => {
      console.log("Store created successfully:", data);

      await queryClient.invalidateQueries({ queryKey: ["stores"] });

      const newStoreId = String(data.id);
      console.log("Setting new store ID:", newStoreId);

      safeStorage.set("selectedStoreId", newStoreId);
      updateSelectedStore(newStoreId);

      navigate("/setup");
    },
    onError: (error) => {
      console.error("Store creation error:", error);
      console.error("Full error object:", JSON.stringify(error, null, 2));
      // A zone the backend won't take belongs on the field, so the merchant
      // knows which input to change.
      if (createErrorMessages(error).some(isTimeZoneMessage)) {
        setErrors((prev) => ({
          ...prev,
          timeZone: t("store.timeZoneRequired"),
        }));
      }
    },
  });

  const makeImageSquare = async (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Make canvas square based on larger dimension
        const size = Math.max(img.width, img.height);
        canvas.width = size;
        canvas.height = size;

        // Make background transparent
        ctx.clearRect(0, 0, size, size);

        // Center the image
        const xOffset = (size - img.width) / 2;
        const yOffset = (size - img.height) / 2;

        // Draw image centered
        ctx.drawImage(img, xOffset, yOffset, img.width, img.height);

        // Convert to data URL
        canvas.toBlob((blob) => {
          resolve(URL.createObjectURL(blob));
        }, "image/png");
      };
      img.src = imageUrl;
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        // Make image square with transparent background
        const squareImageUrl = await makeImageSquare(reader.result);
        setTempImageUrl(squareImageUrl);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  const handleRemoveLogo = (e) => {
    e.stopPropagation();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setLogo(null);
    setPreviewUrl(null);
    setTempImageUrl(null);
    setIsModalOpen(false);
    const fileInput = document.getElementById("logo-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const getCroppedImg = async (image, crop, colorDetection = false) => {
    const canvas = document.createElement("canvas");
    const pixelRatio = window.devicePixelRatio;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: (crop.x * image.width) / 100,
      y: (crop.y * image.height) / 100,
      width: (crop.width * image.width) / 100,
      height: (crop.height * image.height) / 100,
    };

    canvas.width = pixelCrop.width * pixelRatio;
    canvas.height = pixelCrop.height * pixelRatio;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = "high";

    if (colorDetection) {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, pixelCrop.width, pixelCrop.height);
    }

    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }
          blob.name = "cropped-logo.png";
          resolve(blob);
        },
        "image/png",
        1,
      );
    });
  };

  // Convert to hex with proper formatting
  const rgbToHex = (r, g, b) => {
    const toHex = (value) => {
      const hex = Math.max(0, Math.min(255, Math.round(value))).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const extractColorsFromImage = async (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          const width = canvas.width;
          const height = canvas.height;

          // Check for transparency more accurately
          let transparentPixels = 0;
          let semiTransparentPixels = 0;
          let whitePixels = 0;
          let totalPixels = 0;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            totalPixels++;

            if (a === 0) {
              transparentPixels++;
            } else if (a < 200) {
              semiTransparentPixels++;
            }

            if (r === 255 && g === 255 && b === 255) {
              whitePixels++;
            }
          }

          const transparencyRatio = transparentPixels / totalPixels;
          const semiTransparencyRatio = semiTransparentPixels / totalPixels;
          const whiteRatio = whitePixels / totalPixels;

          const hasTransparentBackground =
            transparencyRatio > 0.2 ||
            (transparencyRatio > 0.05 && semiTransparencyRatio > 0.3);

          // Count actual colors from the image with quantization
          const colorCounts = {};

          // Process every pixel
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            // Skip fully transparent pixels
            if (a === 0) continue;

            // Skip white pixels if we have more than 20% (lowered threshold)
            if (r === 255 && g === 255 && b === 255 && whiteRatio > 0.2) {
              continue;
            }

            // Quantize colors to group similar shades (every 16 values)
            const quantizedR = Math.round(r / 16) * 16;
            const quantizedG = Math.round(g / 16) * 16;
            const quantizedB = Math.round(b / 16) * 16;

            const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
            colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
          }

          console.log("Color analysis:", {
            transparencyRatio,
            whiteRatio,
            hasTransparentBackground,
            totalColors: Object.keys(colorCounts).length,
            topColors: Object.entries(colorCounts)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10)
              .map(([color, count]) => ({ color, count })),
          });

          let backgroundColor = null;

          // If image has transparent background, use white
          if (hasTransparentBackground) {
            backgroundColor = [255, 255, 255]; // White background
          } else {
            // Smart background detection: sample left and right middle edges only
            const edgeColors = {};

            // Sample left and right middle edges (avoiding top and bottom)
            const middleY = Math.floor(height / 2);
            const edgePositions = [];

            // Left and right edges around the middle area (middle third of the image height)
            const startY = Math.floor(height / 3);
            const endY = Math.floor((height * 2) / 3);

            for (
              let y = startY;
              y <= endY;
              y += Math.max(1, Math.floor(height / 40))
            ) {
              edgePositions.push({ x: 0, y }); // left edge
              edgePositions.push({ x: width - 1, y }); // right edge
            }

            // Also sample the very middle left and right points
            edgePositions.push({ x: 0, y: middleY });
            edgePositions.push({ x: width - 1, y: middleY });

            // Count colors at edge positions (with quantization)
            edgePositions.forEach(({ x, y }) => {
              const index = (y * width + x) * 4;
              const r = data[index];
              const g = data[index + 1];
              const b = data[index + 2];
              const a = data[index + 3];

              // Skip transparent pixels
              if (a === 0) return;

              // Quantize edge colors too
              const quantizedR = Math.round(r / 16) * 16;
              const quantizedG = Math.round(g / 16) * 16;
              const quantizedB = Math.round(b / 16) * 16;

              const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
              edgeColors[colorKey] = (edgeColors[colorKey] || 0) + 1;
            });

            // Find the most common edge color as background
            if (Object.keys(edgeColors).length > 0) {
              const sortedEdgeColors = Object.entries(edgeColors).sort(
                ([, a], [, b]) => b - a,
              );
              backgroundColor = sortedEdgeColors[0][0].split(",").map(Number);

              console.log(
                "BackgroundColor Sorted Edge Colors:",
                backgroundColor,
              );
            }

            // Fallback: if no edge colors found, use most frequent overall color
            if (!backgroundColor) {
              const sortedColors = Object.entries(colorCounts).sort(
                ([, a], [, b]) => b - a,
              );
              if (sortedColors.length > 0) {
                backgroundColor = sortedColors[0][0].split(",").map(Number);
              } else {
                backgroundColor = [255, 255, 255]; // Default to white if no colors found
              }
            }
          }

          // Sort all colors by frequency
          const sortedColors = Object.entries(colorCounts)
            .sort(([, a], [, b]) => b - a)
            .map(([colorKey]) => colorKey.split(",").map(Number));

          // Function to calculate color difference
          const getColorDifference = (color1, color2) => {
            const rDiff = Math.abs(color1[0] - color2[0]);
            const gDiff = Math.abs(color1[1] - color2[1]);
            const bDiff = Math.abs(color1[2] - color2[2]);
            return rDiff + gDiff + bDiff; // Simple RGB difference
          };

          // Find primary color (most frequent non-background color)
          let primary = null;
          let secondary = null;

          // Look for primary color that's different from background
          for (let i = 0; i < sortedColors.length; i++) {
            const colorDiff = getColorDifference(
              backgroundColor,
              sortedColors[i],
            );
            if (colorDiff > 50) {
              // Different enough from background
              primary = sortedColors[i];
              break;
            }
          }

          // If no distinct primary found, use the most frequent color
          if (!primary && sortedColors.length > 0) {
            primary = sortedColors[0];
          }

          // Fallback primary if still none found
          if (!primary) {
            primary = [100, 100, 100]; // Default gray
          }

          // Look for secondary color that's different from both background and primary
          for (let i = 0; i < sortedColors.length; i++) {
            const colorDiffFromBg = getColorDifference(
              backgroundColor,
              sortedColors[i],
            );
            const colorDiffFromPrimary = getColorDifference(
              primary,
              sortedColors[i],
            );

            if (colorDiffFromBg > 50 && colorDiffFromPrimary > 50) {
              secondary = sortedColors[i];
              break;
            }
          }

          // If no distinct secondary color found, create a complementary one
          if (!secondary) {
            // Create a complementary color by inverting the primary
            secondary = [255 - primary[0], 255 - primary[1], 255 - primary[2]];
          }

          // Convert to hex using the proper function
          const backgroundHex = rgbToHex(
            backgroundColor[0],
            backgroundColor[1],
            backgroundColor[2],
          );
          const primaryHex = rgbToHex(primary[0], primary[1], primary[2]);
          const secondaryHex = rgbToHex(
            secondary[0],
            secondary[1],
            secondary[2],
          );

          console.log("Final colors:", {
            backgroundHex,
            primaryHex,
            secondaryHex,
          });

          resolve({
            background: backgroundHex,
            primary: primaryHex,
            secondary: secondaryHex,
          });
        } catch (error) {
          console.error("Error extracting colors:", error);
          resolve({
            background: "#FFFFFF",
            primary: "#3B82F6",
            secondary: "#1E40AF",
          });
        }
      };
      img.onerror = () => {
        resolve({
          background: "#FFFFFF",
          primary: "#3B82F6",
          secondary: "#1E40AF",
        });
      };
      img.src = imageUrl;
    });
  };

  const handleCropComplete = async () => {
    if (imageRef && crop.width && crop.height) {
      try {
        const croppedImage = await getCroppedImg(imageRef, crop);
        const detectTransparency = await getCroppedImg(imageRef, crop, true);

        if (detectTransparency) {
          // Extract colors from the cropped image
          let colorImageUrl = URL.createObjectURL(detectTransparency);
          const colors = await extractColorsFromImage(colorImageUrl);
          console.log(colors);

          setBackgroundColor(colors.background);
          setPrimaryColor(colors.primary);
          setSecondaryColor(colors.secondary);
        }

        if (croppedImage) {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          const newPreviewUrl = URL.createObjectURL(croppedImage);
          setLogo(croppedImage);
          setPreviewUrl(newPreviewUrl);

          setIsModalOpen(false);
          setTempImageUrl(null);
        }
      } catch (error) {
        console.error("Cropping failed:", error);
      }
    }

    setErrors((prev) => ({
      ...prev,
      logo: null,
    }));
  };

  const onImageLoad = (e) => {
    const { width, height } = e.target;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 100,
        },
        1,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(crop);
    setImageRef(e.target);
  };

  const validateName = (name) => {
    if (!name.trim()) {
      return t("store.nameRequired");
    }
    if (name.trim().length < 2) {
      return t("store.nameMinLength");
    }
    if (name.trim().length > 50) {
      return t("store.nameMaxLength");
    }
    return "";
  };

  const validateLogo = (logoFile) => {
    if (!logoFile) {
      return t("store.logoRequired");
    }
    return "";
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);

    if (touched.name) {
      setErrors((prev) => ({
        ...prev,
        name: validateName(value),
      }));
    }
  };

  const handleNameBlur = () => {
    setTouched((prev) => ({ ...prev, name: true }));
    setErrors((prev) => ({
      ...prev,
      name: validateName(name),
    }));
  };

  // The generic failure box stays for anything the field error doesn't
  // already explain.
  const createErrors = createErrorMessages(createStoreMutation.error);
  const showGenericCreateError =
    createStoreMutation.isError &&
    !(createErrors.length && createErrors.every(isTimeZoneMessage));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nameError = validateName(name);
    const logoError = validateLogo(logo);
    const timeZoneError = timeZone ? "" : t("store.timeZoneRequired");

    setErrors({
      name: nameError,
      logo: logoError,
      timeZone: timeZoneError,
    });

    if (nameError || logoError || timeZoneError) {
      setTouched({ name: true });
      return;
    }

    createStoreMutation.mutate({
      name: name,
      logo: logo,
      currency: currency,
      timeZone: timeZone,
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      backgroundColor: backgroundColor,
    });
  };

  return (
    <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            {t("store.createNewStore")}
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("store.name")}
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    }`}
                    placeholder={t("store.namePlaceholder")}
                  />
                </div>
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("store.logoLabel")}
                </label>
                <div
                  onClick={() => document.getElementById("logo-upload").click()}
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer hover:border-gray-400 ${
                    errors.logo ? "border-red-300 bg-red-50" : "border-gray-300"
                  }`}
                >
                  <div className="space-y-1 text-center">
                    {previewUrl ? (
                      <div className="mb-4">
                        <img
                          src={previewUrl}
                          alt={t("store.logoPreview")}
                          className="mx-auto h-32 w-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="mt-2 text-sm text-red-600 hover:text-red-500"
                        >
                          {t("common.remove")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600 justify-center">
                          <span className="relative font-medium text-indigo-600 hover:text-indigo-500">
                            {t("store.uploadLogo")}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {t("store.logoRequirements")}
                        </p>
                      </>
                    )}
                    <input
                      id="logo-upload"
                      name="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="sr-only"
                    />
                  </div>
                </div>
                {errors.logo && (
                  <p className="mt-2 text-sm text-red-600">{errors.logo}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("store.currency")}
                </label>
                <div className="mt-1">
                  <select
                    id="currency"
                    name="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                      errors.currency ? "border-red-300" : "border-gray-300"
                    }`}
                  >
                    {currencyOptionsFor(currency).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.currency && (
                  <p className="mt-2 text-sm text-red-600">{errors.currency}</p>
                )}
              </div>

              <StoreTimeZoneField
                value={timeZone}
                onChange={(zone) => {
                  setTimeZone(zone);
                  setErrors((prev) => ({ ...prev, timeZone: "" }));
                }}
                savedTimeZone={browserTimeZone}
                t={t}
                required
                error={errors.timeZone}
              />

              {/* Color Preview Section */}
              {imageRef && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("store.brandColors")}
                  </label>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">
                        {t("store.primaryColor")}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="w-8 h-8"
                        />
                        <input
                          type="text"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">
                        {t("store.secondaryColor")}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          className="w-8 h-8"
                        />
                        <input
                          type="text"
                          value={secondaryColor}
                          onChange={(e) => setSecondaryColor(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-500 mb-1">
                        {t("store.backgroundColor")}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="w-8 h-8"
                          placeholder="#FFFFFF"
                        />
                        <input
                          type="text"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="#FFFFFF"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("store.colorsAutoExtracted")}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={createStoreMutation.isPending}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300 disabled:cursor-not-allowed"
              >
                {createStoreMutation.isPending
                  ? t("store.creating")
                  : t("store.create")}
              </button>

              {showGenericCreateError && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {t("errors.createStoreFailed")}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-60"
            onClose={() => setIsModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 mb-4"
                    >
                      Crop Logo
                    </Dialog.Title>

                    <div
                      className="mt-2"
                      style={{
                        backgroundImage: `url(${transparentBackground})`,
                      }}
                    >
                      <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        aspect={1}
                        circularCrop={false}
                        minWidth={100}
                        minHeight={100}
                      >
                        <img
                          src={tempImageUrl}
                          onLoad={onImageLoad}
                          alt="Crop me"
                          style={{ maxWidth: "100%" }}
                        />
                      </ReactCrop>
                    </div>

                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                        onClick={handleCropComplete}
                      >
                        Crop & Save
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsModalOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}
